import { Command } from 'commander';
import chalk from 'chalk';
import Table from 'cli-table3';
import { logger } from '../lib/logger.js';
import {
  checkWsl,
  checkGitInWsl,
  checkTmuxInWsl,
  checkNvmInWsl,
  checkNodeInWsl,
  checkDotnetInWsl,
  checkClaudeInWsl,
  checkSmartStackInWsl,
  checkWindowsTerminal,
  installGitInWsl,
  installTmuxInWsl,
  installNvmInWsl,
  installNodeInWsl,
  installDotnetInWsl,
  installClaudeInWsl,
  installSmartStackInWsl,
  installWindowsTerminal,
  configureWindowsTerminal,
  installStartupScript,
  launchWindowsTerminal,
} from '../lib/wsl-provisioner.js';
import type { ToolStatus, InstallResult } from '../lib/wsl-provisioner.js';

// ============================================================================
// Parent command
// ============================================================================

export const tmuxCommand = new Command('tmux')
  .description('WSL development environment setup (tmux, dotnet, claude, git)')
  .action(() => {
    console.log(chalk.cyan(`
╔══════════════════════════════════════════════╗
║   ${chalk.bold('WSL Dev Environment')} - SmartStack            ║
╚══════════════════════════════════════════════╝
`));
    console.log('Available commands:');
    console.log(`  ${chalk.cyan('ss tmux install')}   Set up complete WSL dev environment`);
    console.log(`  ${chalk.cyan('ss tmux status')}    Check WSL environment status`);
    console.log();
    console.log('Installs: Windows Terminal, git, tmux, nvm, Node.js, .NET 10,');
    console.log('          Claude Code, SmartStack CLI');
    console.log('Then launches Windows Terminal with tmux.');
    console.log();
  });

// ============================================================================
// ss tmux install
// ============================================================================

tmuxCommand
  .command('install')
  .description('Install complete WSL dev environment and launch Claude Code')
  .option('-f, --force', 'Re-install even if already present')
  .option('-v, --verbose', 'Show detailed debug output')
  .option('--no-launch', 'Do not launch Windows Terminal after install')
  .action(async (options) => {
    if (options.verbose) logger.setVerbose(true);

    logger.header('WSL Development Environment Setup');

    const totalSteps = 11;

    // ── Step 1: Install Windows Terminal ──
    logger.step(1, totalSteps, 'Windows Terminal...');

    const wtStatus = checkWindowsTerminal();
    if (wtStatus.installed && !options.force) {
      logger.success('Windows Terminal already installed');
    } else {
      const wtResult = await installWindowsTerminal();
      if (!wtResult.success) {
        logger.error(`Windows Terminal: ${wtResult.error}`);
        logger.info('You can install manually: winget install Microsoft.WindowsTerminal');
        // Non-blocking: continue without WT
      }
    }
    console.log();

    // ── Step 2: Verify WSL ──
    logger.step(2, totalSteps, 'Checking WSL installation...');

    const wslStatus = checkWsl();
    if (!wslStatus.installed) {
      logger.error('WSL is not installed on this system');
      logger.box([
        'WSL is required to continue.',
        '',
        'Install WSL from an admin terminal:',
        '  wsl --install',
        '',
        'Then restart your computer and run ss tmux install again.',
      ], 'error');
      process.exit(1);
    }

    logger.success(`WSL ${wslStatus.version} installed`);
    if (wslStatus.defaultDistro) {
      logger.info(`Default distro: ${wslStatus.defaultDistro}`);
    } else {
      logger.error('No Linux distribution found');
      logger.box([
        'A Linux distribution is required.',
        '',
        'Install Ubuntu:',
        '  wsl --install -d Ubuntu',
      ], 'error');
      process.exit(1);
    }
    console.log();

    // ── Steps 3-8: Install WSL tools ──
    interface ToolDef {
      name: string;
      step: number;
      check: () => ToolStatus;
      install: () => Promise<InstallResult>;
    }

    const tools: ToolDef[] = [
      { name: 'Git', step: 3, check: checkGitInWsl, install: installGitInWsl },
      { name: 'tmux', step: 4, check: checkTmuxInWsl, install: installTmuxInWsl },
      { name: 'nvm', step: 5, check: checkNvmInWsl, install: installNvmInWsl },
      { name: 'Node.js', step: 6, check: checkNodeInWsl, install: installNodeInWsl },
      { name: '.NET SDK 10', step: 7, check: checkDotnetInWsl, install: installDotnetInWsl },
      { name: 'Claude Code', step: 8, check: checkClaudeInWsl, install: installClaudeInWsl },
      { name: 'SmartStack CLI', step: 9, check: checkSmartStackInWsl, install: installSmartStackInWsl },
    ];

    let allSuccess = true;
    const results: { name: string; status: 'ok' | 'error' | 'skipped'; version?: string; error?: string }[] = [];

    for (const tool of tools) {
      logger.step(tool.step, totalSteps, `${tool.name}...`);

      // Check if already installed
      if (!options.force) {
        const status = tool.check();
        if (status.installed) {
          logger.success(`${tool.name} already installed (${status.version})`);
          results.push({ name: tool.name, status: 'skipped', version: status.version! });
          console.log();
          continue;
        }
      }

      // Install
      const result = await tool.install();
      if (result.success) {
        if (result.skipped) {
          logger.success(`${tool.name} already installed (${result.version})`);
          results.push({ name: tool.name, status: 'skipped', version: result.version });
        } else {
          results.push({ name: tool.name, status: 'ok', version: result.version });
        }
      } else {
        logger.error(`Failed to install ${tool.name}: ${result.error}`);
        results.push({ name: tool.name, status: 'error', error: result.error });
        allSuccess = false;
      }
      console.log();
    }

    if (!allSuccess) {
      const failed = results.filter(r => r.status === 'error');
      logger.box([
        `${failed.length} tool(s) failed to install:`,
        ...failed.map(f => `  - ${f.name}: ${f.error}`),
        '',
        'Run again with --verbose for details:',
        '  ss tmux install --verbose',
      ], 'error');
      process.exit(1);
    }

    // ── Step 10: Configure Windows Terminal ──
    logger.step(10, totalSteps, 'Configuring Windows Terminal...');

    const wtCheck = checkWindowsTerminal();
    if (wtCheck.installed) {
      const configResult = configureWindowsTerminal();
      if (configResult.success) {
        if (configResult.profileExists) {
          logger.success('SmartStack Dev profile already configured');
        } else {
          logger.success('SmartStack Dev profile added to Windows Terminal');
        }
      } else {
        logger.warning(`Configuration skipped: ${configResult.error}`);
        logger.info('You can configure manually after launching Windows Terminal once.');
      }
    } else {
      logger.warning('Windows Terminal not available - skipping configuration');
    }

    // Install startup script (tmux + claude auto-launch)
    const scriptResult = installStartupScript();
    if (scriptResult.success) {
      logger.success('Startup script installed (~/.local/bin/smartstack-dev)');
    } else {
      logger.warning(`Startup script: ${scriptResult.error}`);
    }
    console.log();

    // ── Step 11: Launch ──
    logger.step(11, totalSteps, 'Launching...');

    if (options.launch === false) {
      logger.info('Launch skipped (--no-launch)');
      logger.box([
        'WSL development environment ready!',
        '',
        'Launch manually:',
        '  wt -p "SmartStack Dev"',
      ], 'success');
      return;
    }

    if (wtCheck.installed) {
      logger.info('Opening Windows Terminal with SmartStack Dev profile...');
      const launched = launchWindowsTerminal();
      if (launched) {
        logger.box([
          'WSL development environment ready!',
          '',
          'Windows Terminal is opening with tmux.',
          'Run claude inside tmux to start Claude Code.',
          '',
          'Next time, launch directly:',
          '  wt -p "SmartStack Dev"',
        ], 'success');
      } else {
        logger.warning('Could not launch Windows Terminal automatically');
        logger.info('Launch manually: wt -p "SmartStack Dev"');
      }
    } else {
      logger.info('Opening WSL with tmux...');
      logger.box([
        'WSL development environment ready!',
        '',
        'Start manually:',
        '  wsl -e bash -c "tmux new -s smartstack"',
        '  Then run: claude',
      ], 'success');
    }
  });

// ============================================================================
// ss tmux status
// ============================================================================

tmuxCommand
  .command('status')
  .description('Show WSL development environment status')
  .option('--json', 'Output as JSON')
  .option('-v, --verbose', 'Show detailed information')
  .action(async (options) => {
    if (options.verbose) logger.setVerbose(true);

    const wslStatus = checkWsl();
    const wtStatus = checkWindowsTerminal();
    const gitStatus = checkGitInWsl();
    const tmuxStatus = checkTmuxInWsl();
    const nvmStatus = checkNvmInWsl();
    const nodeStatus = checkNodeInWsl();
    const dotnetStatus = checkDotnetInWsl();
    const claudeStatus = checkClaudeInWsl();
    const smartstackStatus = checkSmartStackInWsl();

    // ── JSON output ──
    if (options.json) {
      console.log(JSON.stringify({
        wsl: wslStatus,
        windowsTerminal: wtStatus,
        tools: {
          git: gitStatus,
          tmux: tmuxStatus,
          nvm: nvmStatus,
          node: nodeStatus,
          dotnet: dotnetStatus,
          claude: claudeStatus,
          smartstack: smartstackStatus,
        },
      }, null, 2));
      const allInstalled = wtStatus.installed && gitStatus.installed && tmuxStatus.installed &&
        nodeStatus.installed && dotnetStatus.installed && claudeStatus.installed && smartstackStatus.installed;
      process.exit(allInstalled ? 0 : 1);
    }

    // ── Human-readable ──
    logger.header('WSL Development Environment Status');

    // Windows section
    console.log(chalk.bold('Windows'));
    if (wslStatus.installed) {
      console.log(`  ${chalk.green('✓')} WSL ${wslStatus.version} ${chalk.gray(`(${wslStatus.defaultDistro || 'no distro'})`)}`);
    } else {
      console.log(`  ${chalk.red('✗')} WSL not installed`);
    }
    console.log(`  ${wtStatus.installed ? chalk.green('✓') : chalk.red('✗')} Windows Terminal`);
    console.log();

    // Tools table
    if (!wslStatus.installed) {
      logger.box([
        'WSL is not installed.',
        '',
        'Install WSL: wsl --install',
      ], 'error');
      process.exit(1);
    }

    console.log(chalk.bold('WSL Tools'));
    const table = new Table({
      head: [chalk.cyan('Tool'), chalk.cyan('Status'), chalk.cyan('Version')],
      style: { head: [], border: [] },
      colWidths: [18, 15, 25],
    });

    const tools = [
      { name: 'Git', s: gitStatus },
      { name: 'tmux', s: tmuxStatus },
      { name: 'nvm', s: nvmStatus },
      { name: 'Node.js', s: nodeStatus },
      { name: '.NET SDK', s: dotnetStatus },
      { name: 'Claude Code', s: claudeStatus },
      { name: 'SmartStack CLI', s: smartstackStatus },
    ];

    for (const t of tools) {
      table.push([
        t.name,
        t.s.installed ? chalk.green('✓ OK') : chalk.red('✗ Missing'),
        t.s.version || chalk.gray('-'),
      ]);
    }

    console.log(table.toString());
    console.log();

    // Summary
    const allInstalled = tools.every(t => t.s.installed) && wtStatus.installed;
    if (allInstalled) {
      logger.box([
        'All tools installed!',
        '',
        'Launch: wt -p "SmartStack Dev"',
      ], 'success');
    } else {
      const missing: string[] = [];
      if (!wtStatus.installed) missing.push('Windows Terminal');
      missing.push(...tools.filter(t => !t.s.installed).map(t => t.name));
      logger.box([
        `Missing: ${missing.join(', ')}`,
        '',
        `Fix with: ${chalk.cyan('ss tmux install')}`,
      ], 'warning');
      process.exit(1);
    }
  });
